<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useClipboard } from '@vueuse/core';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { PrintSource, TicketPrintSetting } from '@/enums/PrintEnum';
  import { useV2PrintingUi } from '@/composition/printing/use-printing-v2-ui';
  import {
    buildPrintSourceItems,
    buildPrintTriggerItems,
  } from '@/composition/printing/print-condition-items';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiListbox, {
    IPosUiListboxItem,
  } from '@/components/pos-v2/ui/PosUiListbox.vue';

  type TDestination = 'ticket' | 'comanda' | 'sticker';

  const ui = useUi();
  const {
    v2Store,
    bridge,
    restaurantStore,
    isConnected,
    canEdit,
    logicalById,
    bridgePrinterByRef,
  } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // #region printer assignment items

  const escposPrinterItems = computed<IPosUiListboxItem[]>(() =>
    (v2Store.displayConfig?.logicalPrinters ?? [])
      .filter((lp) => lp.format === 'escpos')
      .map((lp) => ({ id: lp.id, label: lp.name }))
  );

  const zplPrinterItems = computed<IPosUiListboxItem[]>(() =>
    (v2Store.displayConfig?.logicalPrinters ?? [])
      .filter((lp) => lp.format === 'zpl')
      .map((lp) => ({ id: lp.id, label: lp.name }))
  );

  const assignments = computed(
    () =>
      v2Store.displayConfig?.assignments ?? {
        ticket: null,
        sticker: null,
        comandaByStation: {} as Record<number, number | null>,
      }
  );

  async function setAssignment(
    destination: TDestination,
    stationId: number | null,
    value: number | string | null
  ) {
    const logicalId = value === null ? null : Number(value);
    await v2Store.mutateConfig((draft) => {
      if (destination === 'ticket') draft.assignments.ticket = logicalId;
      else if (destination === 'sticker') draft.assignments.sticker = logicalId;
      else if (stationId !== null) {
        draft.assignments.comandaByStation = {
          ...draft.assignments.comandaByStation,
          [stationId]: logicalId,
        };
      }
    });
  }

  // #endregion

  // #region assignment slot rows + health chips

  interface ISlotChip {
    color: 'success' | 'error' | 'warning' | 'info';
    labelKey: string;
  }

  interface IAssignmentSlot {
    key: string;
    destination: TDestination;
    stationId: number | null;
    label: string;
    assignedId: number | null;
    items: IPosUiListboxItem[];
    chip: ISlotChip;
  }

  /**
   * Health chip resolution, worst-first: error "Sin conexión" (assigned
   * printer offline / not found on the bridge / error / paper-out),
   * warning "Sin impresora asignada", info "Se recomienda una impresora de
   * etiquetas" (sticker slot holding an escpos printer), else success OK.
   */
  function chipOf(
    destination: TDestination,
    assignedId: number | null
  ): ISlotChip {
    if (assignedId === null) {
      return { color: 'warning', labelKey: 'printingV2.healthUnassigned' };
    }
    const logical = logicalById.value.get(assignedId) ?? null;
    const bridgePrinter = logical
      ? bridgePrinterByRef.value.get(logical.ref)
      : undefined;
    const paperOut = !!bridgePrinter?.statusDetail?.includes('PAPER_OUT');
    if (
      !logical ||
      !bridgePrinter ||
      bridgePrinter.status === 'offline' ||
      bridgePrinter.status === 'error' ||
      paperOut
    ) {
      return { color: 'error', labelKey: 'printingV2.healthOffline' };
    }
    if (destination === 'sticker' && logical.format === 'escpos') {
      return { color: 'info', labelKey: 'printingV2.healthStickerFormat' };
    }
    return { color: 'success', labelKey: 'printingV2.healthOk' };
  }

  const assignmentSlots = computed<IAssignmentSlot[]>(() => {
    const slots: IAssignmentSlot[] = [];
    const current = assignments.value;

    slots.push({
      key: 'ticket',
      destination: 'ticket',
      stationId: null,
      label: i18nT('printingV2.destTicket'),
      assignedId: current.ticket,
      items: escposPrinterItems.value,
      chip: chipOf('ticket', current.ticket),
    });

    for (const station of restaurantStore.printStations) {
      const assignedId = current.comandaByStation[station.stationId] ?? null;
      slots.push({
        key: `comanda:${station.stationId}`,
        destination: 'comanda',
        stationId: station.stationId,
        label: station.name,
        assignedId,
        items: escposPrinterItems.value,
        chip: chipOf('comanda', assignedId),
      });
    }

    slots.push({
      key: 'sticker',
      destination: 'sticker',
      stationId: null,
      label: i18nT('printingV2.destSticker'),
      assignedId: current.sticker,
      items: zplPrinterItems.value,
      chip: chipOf('sticker', current.sticker),
    });

    return slots;
  });

  // #endregion

  // #region trigger / source items

  // One shared list per field for every destination — see print-condition-items.ts.
  const triggerItems = computed<IPosUiListboxItem[]>(() =>
    buildPrintTriggerItems(i18nT)
  );
  const sourceItems = computed<IPosUiListboxItem[]>(() =>
    buildPrintSourceItems(i18nT)
  );

  interface IConditionsGroup {
    key: TDestination;
    title: string;
  }

  /** One "Condiciones" group per destination, in display order. */
  const conditionsGroups = computed<IConditionsGroup[]>(() => [
    { key: 'ticket', title: i18nT('printingV2.destTicket') },
    { key: 'comanda', title: i18nT('printingV2.sectionComandas') },
    { key: 'sticker', title: i18nT('printingV2.destSticker') },
  ]);

  const destinationSettings = computed(
    () => v2Store.displayConfig?.destinationSettings ?? null
  );

  async function setSetting(
    destination: TDestination,
    field: 'trigger' | 'source',
    value: number | string | null
  ) {
    if (value === null) return;
    // Listbox ids are the enum members themselves — the cast just restores
    // the type the generic listbox widened to number | string.
    await v2Store.mutateConfig((draft) => {
      if (field === 'trigger') {
        draft.destinationSettings[destination].trigger = Number(
          value
        ) as TicketPrintSetting;
      } else {
        draft.destinationSettings[destination].source = Number(
          value
        ) as PrintSource;
      }
    });
  }

  // #endregion

  // #region advanced

  const advancedOpen = ref(false);

  const identity = computed(() => bridge.value?.identity.value ?? null);
  const updateState = computed(() => bridge.value?.updateState.value ?? null);

  const instanceName = ref('');
  const instanceNameDirty = ref(false);
  const savingName = ref(false);

  const displayedInstanceName = computed(() =>
    instanceNameDirty.value
      ? instanceName.value
      : identity.value?.instanceName ?? ''
  );

  function onInstanceNameInput(value: string) {
    instanceName.value = value;
    instanceNameDirty.value = true;
  }

  async function saveInstanceName() {
    const name = instanceName.value.trim();
    if (!name || !bridge.value) return;
    savingName.value = true;
    try {
      await bridge.value.setInstanceName(name);
      instanceNameDirty.value = false;
      ui.showToast(AlertType.Success, i18nT('printingV2.saved'));
    } catch (e) {
      ui.showToast(AlertType.Error, i18nT('printingV2.renameFailed'));
    } finally {
      savingName.value = false;
    }
  }

  const updateStatusText = computed(() => {
    const state = updateState.value;
    if (!state || state.phase === 'idle') return '';
    switch (state.phase) {
      case 'checking':
        return i18nT('printingV2.updateChecking');
      case 'available':
      case 'downloading':
        return i18nT('printingV2.updateDownloading', {
          percent: Math.round(state.percent ?? 0),
        });
      case 'downloaded':
        return i18nT('printingV2.updateDownloaded');
      case 'not-available':
        return i18nT('printingV2.updateNone');
      case 'error':
        return i18nT('printingV2.updateError');
      default:
        return '';
    }
  });

  const checkingUpdate = ref(false);
  const applyingUpdate = ref(false);

  async function checkUpdates() {
    if (!bridge.value) return;
    checkingUpdate.value = true;
    try {
      await bridge.value.checkForUpdate();
    } catch (e) {
      ui.showToast(AlertType.Error, i18nT('printingV2.updateError'));
    } finally {
      checkingUpdate.value = false;
    }
  }

  async function applyUpdate() {
    if (!bridge.value) return;
    applyingUpdate.value = true;
    try {
      await bridge.value.applyUpdate();
    } catch (e) {
      ui.showToast(AlertType.Error, i18nT('printingV2.updateError'));
      applyingUpdate.value = false;
    }
  }

  const clipboard = useClipboard();
  const copyingDiag = ref(false);

  async function copyDiagnostics() {
    if (!bridge.value) return;
    copyingDiag.value = true;
    try {
      const [snapshot, logsTail] = await Promise.all([
        bridge.value.diagSnapshot(),
        bridge.value.diagLogsTail(500),
      ]);
      await clipboard.copy(JSON.stringify({ snapshot, logsTail }, null, 2));
      ui.showToast(AlertType.Success, i18nT('printingV2.diagCopied'));
    } catch (e) {
      console.error('[PrintingV2] diagnostics copy failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.actionFailed'));
    } finally {
      copyingDiag.value = false;
    }
  }

  // #endregion
</script>

<template>
  <div class="v2-preferences-tab">
    <div v-if="v2Store.configReadOnly" class="preferences-readonly">
      <PosUiIcon name="alert" class="preferences-readonly-icon" />
      {{ $t('printingV2.readOnlyHint') }}
    </div>

    <template v-if="destinationSettings">
      <!-- Asignaciones: one row per slot with a live health chip -->
      <section class="preferences-section">
        <h4 class="section-title">{{ $t('printingV2.sectionAssignments') }}</h4>
        <div class="assignment-rows">
          <div
            v-for="slot in assignmentSlots"
            :key="slot.key"
            class="assignment-row"
          >
            <div class="assignment-row-head">
              <span class="assignment-row-label">{{ slot.label }}</span>
              <PosUiBadge :color="slot.chip.color" variant="soft" size="xs">
                {{ $t(slot.chip.labelKey) }}
              </PosUiBadge>
            </div>
            <PosUiListbox
              :value="slot.assignedId"
              :items="slot.items"
              clearable
              :placeholder="$t('printingV2.noPrinterOption')"
              :disabled="!canEdit"
              class="assignment-row-listbox"
              @input="setAssignment(slot.destination, slot.stationId, $event)"
            />
          </div>
        </div>
      </section>

      <!-- Condiciones: trigger + source per destination -->
      <section class="preferences-section">
        <h4 class="section-title">{{ $t('printingV2.sectionConditions') }}</h4>
        <div class="conditions-grid">
          <div
            v-for="group in conditionsGroups"
            :key="group.key"
            class="conditions-group"
          >
            <div class="conditions-group-title">{{ group.title }}</div>
            <div class="section-field">
              <label class="section-label">{{
                $t('printingV2.fieldTrigger')
              }}</label>
              <PosUiListbox
                :value="destinationSettings[group.key].trigger"
                :items="triggerItems"
                :disabled="!canEdit"
                @input="setSetting(group.key, 'trigger', $event)"
              />
            </div>
            <div class="section-field">
              <label class="section-label">{{
                $t('printingV2.fieldSource')
              }}</label>
              <PosUiListbox
                :value="destinationSettings[group.key].source"
                :items="sourceItems"
                :disabled="!canEdit"
                @input="setSetting(group.key, 'source', $event)"
              />
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- Avanzado -->
    <section class="preferences-advanced">
      <button
        class="advanced-toggle"
        type="button"
        @click="advancedOpen = !advancedOpen"
      >
        <PosUiIcon
          :name="advancedOpen ? 'chevron-down' : 'chevron-right'"
          class="advanced-toggle-icon"
        />
        {{ $t('printingV2.advanced') }}
      </button>

      <div v-if="advancedOpen" class="advanced-body">
        <div class="section-field">
          <label class="section-label">{{
            $t('printingV2.instanceName')
          }}</label>
          <div class="advanced-name-row">
            <PosUiInput
              :value="displayedInstanceName"
              :maxlength="40"
              :disabled="!isConnected"
              @input="onInstanceNameInput"
              @enter="saveInstanceName"
            />
            <PosUiButton
              color="neutral"
              variant="outline"
              :loading="savingName"
              :disabled="!isConnected || !instanceNameDirty"
              @click="saveInstanceName"
            >
              {{ $t('printingV2.save') }}
            </PosUiButton>
          </div>
        </div>

        <div class="section-field">
          <label class="section-label">{{
            $t('printingV2.pluginVersion')
          }}</label>
          <div class="advanced-update-row">
            <span class="advanced-version">
              {{ identity ? `v${identity.version}` : '—' }}
            </span>
            <span v-if="updateStatusText" class="advanced-update-status">
              {{ updateStatusText }}
            </span>
            <PosUiButton
              v-if="updateState && updateState.phase === 'downloaded'"
              color="primary"
              size="sm"
              :loading="applyingUpdate"
              :disabled="!isConnected"
              @click="applyUpdate"
            >
              {{ $t('printingV2.applyUpdate') }}
            </PosUiButton>
            <PosUiButton
              v-else
              color="neutral"
              variant="outline"
              size="sm"
              :loading="checkingUpdate"
              :disabled="!isConnected"
              @click="checkUpdates"
            >
              {{ $t('printingV2.checkUpdates') }}
            </PosUiButton>
          </div>
        </div>

        <div class="section-field">
          <PosUiButton
            color="neutral"
            variant="outline"
            size="sm"
            icon="content-copy"
            :loading="copyingDiag"
            :disabled="!isConnected"
            @click="copyDiagnostics"
          >
            {{ $t('printingV2.copyDiag') }}
          </PosUiButton>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
  .v2-preferences-tab {
    @apply tw-flex tw-flex-col tw-gap-4;

    .preferences-readonly {
      @apply tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-2.5 tw-text-sm tw-text-amber-700;

      .preferences-readonly-icon {
        @apply tw-text-base;
      }
    }

    .preferences-section {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4;

      .section-title {
        @apply tw-m-0 tw-mb-3 tw-text-sm tw-font-bold tw-text-neutral-900;
      }
    }

    .assignment-rows {
      @apply tw-flex tw-flex-col tw-gap-2.5;

      .assignment-row {
        @apply tw-flex tw-flex-col tw-gap-1.5 tw-rounded-lg tw-border tw-border-neutral-100 tw-p-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between;

        .assignment-row-head {
          @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2;

          .assignment-row-label {
            @apply tw-truncate tw-text-sm tw-font-medium tw-text-neutral-800;
          }
        }

        .assignment-row-listbox {
          @apply tw-w-full sm:tw-w-64;
        }
      }
    }

    .conditions-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3;

      .conditions-group {
        @apply tw-flex tw-flex-col tw-gap-3;

        .conditions-group-title {
          @apply tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-400;
        }
      }
    }

    .section-field {
      @apply tw-flex tw-flex-col tw-gap-1.5;

      .section-label {
        @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }
    }

    .preferences-advanced {
      .advanced-toggle {
        @apply tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-text-neutral-600 hover:tw-text-neutral-900;

        .advanced-toggle-icon {
          @apply tw-text-lg;
        }
      }

      .advanced-body {
        @apply tw-mt-3 tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4;

        .section-field {
          @apply tw-flex tw-flex-col tw-gap-1.5;

          .section-label {
            @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
          }
        }

        .advanced-name-row {
          @apply tw-flex tw-max-w-md tw-items-center tw-gap-2;
        }

        .advanced-update-row {
          @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3;

          .advanced-version {
            @apply tw-text-sm tw-font-medium tw-text-neutral-700;
          }

          .advanced-update-status {
            @apply tw-text-xs tw-text-neutral-500;
          }
        }
      }
    }
  }
</style>
